<template>    
     
    <td class="px-3">
        <button class="btn btn-link p-0 text-decoration-none" @click="viewDetails(props.value)">
            <span class="px-3 link-info"
                v-if="props.value.tag && props.value.tag.includes('SOMA')"><b>TANQUE SOMA</b></span>
            <span class="px-3 link-info" v-else><b>{{
                    props.value.name ? props.value.name.toUpperCase() : props.value.tag
                }}</b></span>
        </button>
    </td>
    <td class="text-center">
        {{ props.value.content }}
    </td>
    <td class="text-center" >
        <div v-if="props.value.productDensity">
            {{ floatToDecimal(props.value.currentVolume * props.value.productDensity) }} / {{ floatToDecimal(props.value.capacity * props.value.productDensity) }} ton
        </div>
        {{ floatToDecimal(props.value.currentVolume) }} / {{ floatToDecimal(props.value.capacity) }} {{ props.value.unit }}
    </td>
    
    <td class="text-center w-120">
        <div class="progress-container">
            <div class="progress custom-progress">
                <div 
                    class="progress-bar animated-progress"
                    :class="{
                        'bg-success': parseInt(props.value.level) > 0 && props.value.level == 1,
                        'bg-warning': parseInt(props.value.level) > 0 && props.value.level == 2,
                        'bg-danger': parseInt(props.value.level) > 0 && props.value.level == 3,
                        'bg-info': parseInt(props.value.level) === 0
                    }"
                    :style="{ width: progressWidth + '%' }"
                    role="progressbar"
                    :aria-valuenow="props.value.currentVolume / props.value.capacity * 100"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                </div>
            </div>
            <div class="progress-label">{{ floatToDecimal(displayPercentage) }} %</div>
        </div>
    </td>
    <td class="text-center">
        <span 
            class="badge text-white fs-12"
            :class="{
                'bg-success': parseInt(props.value.level) > 0 && props.value.level == 1,
                'bg-warning': parseInt(props.value.level) > 0 && props.value.level == 2,
                'bg-danger': parseInt(props.value.level) > 0 && props.value.level == 3,
                'bg-info': parseInt(props.value.level) === 0
            }"
        >
            {{
                parseInt(props.value.level) > 0 ?
                    (props.value.level == 1 ? 'NORMAL' :
                        (
                            props.value.level == 2 ? 'RECARGA' : 'CRÍTICO'
                        ))
                    : 'INDEFINIDO'
            }}
        </span>
    </td>
    <td class="text-center">
        <div v-if="props.value.abruptChange" class="abrupt-change-alert">
            <font-awesome-icon :icon="faChartLine" class="pulse-icon me-1" />
            <small class="fw-bold">
                Variação Abrupta<br>
                <span class="pulse-text">{{ props.value.abruptChangePercentage?.toFixed(1).replace('.', ',') }}%</span>
            </small>
        </div>
        <span v-else class="text-muted small">-</span>
    </td>
    <td class="text-center">
        <font-awesome-icon v-if="isAtualizacaoAntiga" :icon="faExclamationCircle" class="text-danger" :size="'lg'" />
        <span v-else></span>
        {{ props.value.updated }}
    </td>
</template>

<script setup>
import {defineProps, defineEmits, computed, ref, onMounted} from 'vue';
import {floatToDecimal} from "./Dependencies/functions";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faExclamationCircle, faChartLine } from '@fortawesome/free-solid-svg-icons';

const props = defineProps({
    value: {
        type: Object,
        required: true
    },
    isFavorite: {
        type: Boolean,
        required: true
    }
});

const emit = defineEmits(["removeFavorite", "addFavorite", "viewDetails"]);

// Animação da barra de progresso
const progressWidth = ref(0);
const displayPercentage = ref(0);

const targetPercentage = computed(() => {
    return (props.value.currentVolume / props.value.capacity) * 100;
});

onMounted(() => {
    // Inicia a animação após um pequeno delay
    setTimeout(() => {
        animateProgress();
    }, 200);
});

const animateProgress = () => {
    const duration = 2000; // 2 segundos
    const startTime = performance.now();
    
    // Função de easing para suavizar a animação
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
    
    const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Aplica a função de easing
        const easedProgress = easeOutQuart(progress);
        
        progressWidth.value = easedProgress * targetPercentage.value;
        displayPercentage.value = progressWidth.value;
        
        if (progress < 1) {
            requestAnimationFrame(animate);
        } else {
            progressWidth.value = targetPercentage.value;
            displayPercentage.value = targetPercentage.value;
        }
    };
    
    requestAnimationFrame(animate);
};

const favorite = (value) => {
    if (props.isFavorite) {
        emit('removeFavorite', value)
    } else {
        emit('addFavorite', value)
    }
}

const viewDetails = (tank) => {
    emit('viewDetails', tank);
}

const compFavorite = computed(() => props.isFavorite)

const isAtualizacaoAntiga = computed(() => {
    if (!props.value.updatedAt) return false;
    const dataAtualizacao = new Date(props.value.updatedAt);
    const agora = new Date();
    const diferencaMinutos = (agora - dataAtualizacao) / (1000 * 60);
    return diferencaMinutos > 60;
});
</script>

<style scoped>
.w-120 {
    width: 120px!important;
}

.progress-container {
    position: relative;
    width: 100%;
}

.custom-progress {
    height: 18px;
    background-color: #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.progress-bar {
    height: 100%;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background-image: linear-gradient(45deg, transparent 25%, rgba(255,255,255,.2) 25%, rgba(255,255,255,.2) 50%, transparent 50%, transparent 75%, rgba(255,255,255,.2) 75%, rgba(255,255,255,.2));
    background-size: 15px 15px;
}

.animated-progress {
    position: relative;
    overflow: hidden;
}

.animated-progress::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    animation: progressShine 2s ease-in-out;
}

@keyframes progressShine {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.progress-label {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 11px;
    font-weight: 600;
    color: #000;
    text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
    z-index: 10;
    white-space: nowrap;
}

.fs-12 {
    font-size: 12px !important;
}

/* Animação pulsante para variação abrupta */
@keyframes pulse {
    0% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.6;
        transform: scale(1.05);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

.abrupt-change-alert {
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(220, 53, 69, 0.05));
    border: 1px solid rgba(220, 53, 69, 0.3);
    border-radius: 8px;
    padding: 8px;
    color: #dc3545;
    animation: pulse 2s ease-in-out infinite;
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.2);
    display: inline-block;
    min-width: 100px;
}

.pulse-icon {
    color: #dc3545;
    animation: pulse 1.5s ease-in-out infinite;
}

.pulse-text {
    color: #dc3545;
    font-weight: bold;
    font-size: 0.85rem;
}
</style>
